/**
 * Consent Forms Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { FileSignature, Save, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export function ConsentFormsOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    consents: [],
    signer_name: "",
    signer_relationship: "",
    witnessed_by: "",
    date_signed: new Date().toISOString().split("T")[0],
  });

  const consentForms = [
    { id: "hospice_election", name: "Hospice Election Statement", required: true, description: "Patient elects hospice benefit" },
    { id: "plan_of_care", name: "Plan of Care Consent", required: true, description: "Consent to POC and services" },
    { id: "dnr", name: "DNR/POLST", required: false, description: "Do Not Resuscitate order" },
    { id: "hipaa", name: "HIPAA Authorization", required: true, description: "Privacy practices acknowledgment" },
    { id: "medication", name: "Medication Consent", required: true, description: "Consent for medication management" },
    { id: "photo_video", name: "Photo/Video Release", required: false, description: "Permission for documentation" },
    { id: "emergency_contact", name: "Emergency Contact Auth", required: true, description: "Authorization to contact" },
    { id: "financial", name: "Financial Agreement", required: true, description: "Billing and payment terms" },
    { id: "advance_directive", name: "Advance Directive", required: false, description: "Living will/healthcare proxy" },
    { id: "bereavement", name: "Bereavement Services", required: false, description: "Consent for family support" },
  ];

  const toggleConsent = (consentId) => {
    if (formData.consents.includes(consentId)) {
      setFormData({
        ...formData,
        consents: formData.consents.filter(c => c !== consentId)
      });
    } else {
      setFormData({
        ...formData,
        consents: [...formData.consents, consentId]
      });
    }
  };

  const handleSave = () => {
    const requiredMissing = consentForms
      .filter(c => c.required && !formData.consents.includes(c.id));
    
    if (requiredMissing.length > 0) {
      toast.error(`Missing required consents: ${requiredMissing.map(c => c.name).join(", ")}`);
      return;
    }
    
    if (!formData.signer_name) {
      toast.error("Please enter signer name");
      return;
    }
    
    toast.success("Consent forms documented");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  const requiredCount = consentForms.filter(c => c.required).length;
  const completedRequired = consentForms.filter(c => c.required && formData.consents.includes(c.id)).length;

  return (
    <div className="space-y-4 p-4" data-testid="consent-forms-overlay">
      <div className="flex items-center gap-2 mb-4">
        <FileSignature className="w-5 h-5 text-purple-400" />
        <h3 className="font-semibold text-white">Consent Forms</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
        <div>
          <p className="text-sm font-medium text-slate-200">Required Consents Progress</p>
          <p className="text-xs text-slate-400">{completedRequired} of {requiredCount} required forms obtained</p>
        </div>
        <Badge className={completedRequired === requiredCount ? "bg-green-500" : "bg-amber-500"}>
          {completedRequired}/{requiredCount}
        </Badge>
      </div>

      <ScrollArea className="h-[300px] pr-4">
        <div className="space-y-2">
          {consentForms.map((consent) => (
            <div 
              key={consent.id}
              className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                formData.consents.includes(consent.id)
                  ? "bg-teal-500/10 border-teal-500/30"
                  : "bg-slate-800/50 border-slate-700 hover:bg-slate-800"
              }`}
              onClick={() => toggleConsent(consent.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={formData.consents.includes(consent.id)}
                    onCheckedChange={() => toggleConsent(consent.id)}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-200">{consent.name}</p>
                      {consent.required && (
                        <Badge variant="outline" className="text-xs border-red-500/50 text-red-400">Required</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{consent.description}</p>
                  </div>
                </div>
                {formData.consents.includes(consent.id) && (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Signer Name</Label>
          <Input 
            value={formData.signer_name} 
            onChange={(e) => setFormData({...formData, signer_name: e.target.value})} 
            placeholder="Full legal name" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Relationship to Patient</Label>
          <Input 
            value={formData.signer_relationship} 
            onChange={(e) => setFormData({...formData, signer_relationship: e.target.value})} 
            placeholder="Self, Spouse, POA..." 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Witnessed By</Label>
          <Input 
            value={formData.witnessed_by} 
            onChange={(e) => setFormData({...formData, witnessed_by: e.target.value})} 
            placeholder="Witness name" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Date Signed</Label>
          <Input 
            type="date" 
            value={formData.date_signed} 
            onChange={(e) => setFormData({...formData, date_signed: e.target.value})} 
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Consents</Button>
      </div>
    </div>
  );
}

export default ConsentFormsOverlay;
